import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, X, Upload, GripVertical } from "lucide-react";
import api, { inr, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  name: "", description: "", price: 0, mrp: 0, audience: "men", category: "kurta",
  fabric: "Cotton", color: "Ivory", occasion: "Casual", sizes: [], images: [],
  care: "Dry clean recommended.", stock: 20, featured: false, new_arrival: false, festive: false, active: true,
};
const MEN_SIZES = ["S", "M", "L", "XL", "XXL"];
const BOYS_SIZES = ["2-3Y", "4-5Y", "6-7Y", "8-9Y", "10-11Y"];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/products").then(({ data }) => setProducts(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditId(p.id); setOpen(true); };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(fileUrl(data.url));
      }
      setForm((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
      toast.success("Image(s) uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addUrl = () => {
    const url = prompt("Paste image URL");
    if (url) setForm((prev) => ({ ...prev, images: [...prev.images, url] }));
  };

  const removeImg = (i) => setForm((prev) => ({ ...prev, images: prev.images.filter((_, idx) => idx !== i) }));
  const moveImg = (i, dir) => {
    setForm((prev) => {
      const imgs = [...prev.images];
      const j = i + dir;
      if (j < 0 || j >= imgs.length) return prev;
      [imgs[i], imgs[j]] = [imgs[j], imgs[i]];
      return { ...prev, images: imgs };
    });
  };

  const toggleSize = (s) => setForm((prev) => ({ ...prev, sizes: prev.sizes.includes(s) ? prev.sizes.filter((x) => x !== s) : [...prev.sizes, s] }));

  const save = async () => {
    if (!form.name || form.images.length === 0 || form.sizes.length === 0) return toast.error("Name, at least one image and one size are required");
    const payload = { ...form, price: Number(form.price), mrp: Number(form.mrp), stock: Number(form.stock) };
    try {
      if (editId) await api.put(`/admin/products/${editId}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editId ? "Product updated" : "Product created");
      setOpen(false);
      load();
    } catch {
      toast.error("Could not save product");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  const sizeOptions = form.audience === "boys" ? BOYS_SIZES : MEN_SIZES;

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-serif text-4xl text-[var(--bronze)]">Products</h1><p className="text-[var(--olive)]">{products.length} items in catalogue</p></div>
        <button onClick={openNew} data-testid="add-product-btn" className="btn-primary flex items-center gap-2 px-6 py-3 uplabel"><Plus className="w-4 h-4" /> Add Product</button>
      </div>

      <div className="border border-[var(--line)] overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#F3EECB]"><tr className="uplabel text-[0.6rem] text-left">
            <th className="p-4">Product</th><th className="p-4">Audience</th><th className="p-4">Price</th><th className="p-4">Stock</th><th className="p-4">Flags</th><th className="p-4"></th>
          </tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} data-testid={`admin-product-${p.id}`} className="border-t border-[var(--line)]">
                <td className="p-4"><div className="flex items-center gap-3"><img src={p.images[0]} alt="" className="w-12 aspect-[3/4] object-cover" /><span className="text-[var(--bronze)]">{p.name}</span></div></td>
                <td className="p-4 text-[var(--bronze)] capitalize">{p.audience} · {p.category.replace("-", " ")}</td>
                <td className="p-4 text-[var(--bronze)]">{inr(p.price)}</td>
                <td className="p-4 text-[var(--bronze)]">{p.stock}</td>
                <td className="p-4"><div className="flex gap-1 flex-wrap">{p.featured && <span className="uplabel text-[0.55rem] bg-[var(--olive)] text-[#FBF7DE] px-1.5 py-0.5">Feat</span>}{p.new_arrival && <span className="uplabel text-[0.55rem] bg-[#F3EECB] px-1.5 py-0.5">New</span>}{p.festive && <span className="uplabel text-[0.55rem] bg-[var(--maroon)] text-[#FBF7DE] px-1.5 py-0.5">Fest</span>}</div></td>
                <td className="p-4"><div className="flex gap-3 justify-end">
                  <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="text-[var(--olive)] hover:text-[var(--bronze)]"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} data-testid={`delete-product-${p.id}`} className="text-[var(--olive)] hover:text-[var(--maroon)]"><Trash2 className="w-4 h-4" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FBF7DE] border-[var(--line)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[var(--bronze)]">{editId ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" className={inputCls} /></Field>
            <Field label="Description"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Price (₹)"><input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" className={inputCls} /></Field>
              <Field label="MRP (₹)"><input type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className={inputCls} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Audience"><SelSm value={form.audience} onChange={(v) => setForm({ ...form, audience: v, sizes: [] })} options={[["men", "Men"], ["boys", "Boys"]]} /></Field>
              <Field label="Category"><SelSm value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={[["kurta", "Kurta"], ["kurta-set", "Kurta Set"]]} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Fabric"><input value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })} className={inputCls} /></Field>
              <Field label="Colour"><input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className={inputCls} /></Field>
              <Field label="Occasion"><SelSm value={form.occasion} onChange={(v) => setForm({ ...form, occasion: v })} options={[["Casual", "Casual"], ["Festive", "Festive"], ["Wedding", "Wedding"], ["Evening", "Evening"]]} /></Field>
            </div>
            <Field label="Stock"><input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inputCls} /></Field>

            <Field label="Sizes">
              <div className="flex flex-wrap gap-2">
                {sizeOptions.map((s) => (
                  <button key={s} type="button" onClick={() => toggleSize(s)} data-testid={`size-toggle-${s}`}
                    className={`px-3 py-2 text-sm border transition-colors ${form.sizes.includes(s) ? "bg-[var(--olive)] text-[#FBF7DE] border-[var(--olive)]" : "border-[var(--line)] text-[var(--bronze)]"}`}>{s}</button>
                ))}
              </div>
            </Field>

            <Field label="Images">
              <div className="flex flex-wrap gap-3 mb-3">
                {form.images.map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={img} alt="" className="w-20 aspect-[3/4] object-cover border border-[var(--line)]" />
                    <button onClick={() => removeImg(i)} className="absolute -top-2 -right-2 bg-[var(--maroon)] text-[#FBF7DE] rounded-full w-5 h-5 grid place-items-center"><X className="w-3 h-3" /></button>
                    <div className="flex justify-between mt-1">
                      <button onClick={() => moveImg(i, -1)} className="text-xs text-[var(--olive)]">◀</button>
                      <button onClick={() => moveImg(i, 1)} className="text-xs text-[var(--olive)]">▶</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} data-testid="product-image-upload" className="hidden" id="img-up" />
                <label htmlFor="img-up" className="flex items-center gap-2 border border-[var(--line)] px-4 py-2 text-sm cursor-pointer text-[var(--bronze)] hover:border-[var(--olive)] transition-colors">
                  <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload"}
                </label>
                <button type="button" onClick={addUrl} className="border border-[var(--line)] px-4 py-2 text-sm text-[var(--bronze)]">Add URL</button>
              </div>
            </Field>

            <div className="flex gap-6 flex-wrap">
              {[["featured", "Featured"], ["new_arrival", "New Arrival"], ["festive", "Festive"], ["active", "Active"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-[var(--bronze)]">
                  <input type="checkbox" checked={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="accent-[var(--olive)]" /> {l}
                </label>
              ))}
            </div>

            <button onClick={save} data-testid="save-product-btn" className="btn-primary w-full uplabel py-3.5 mt-2">{editId ? "Update Product" : "Create Product"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inputCls = "w-full bg-transparent border border-[var(--line)] px-3 py-2 outline-none text-[var(--bronze)] focus:border-[var(--olive)] transition-colors";
const Field = ({ label, children }) => (<div><label className="uplabel text-[0.6rem] block mb-1.5">{label}</label>{children}</div>);
const SelSm = ({ value, onChange, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="border-[var(--line)] bg-transparent text-[var(--bronze)]"><SelectValue /></SelectTrigger>
    <SelectContent className="bg-[#FBF7DE] border-[var(--line)]">{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);
